import React, { useState, useEffect, useContext, useRef } from "react";
import Select from "react-select";
import {
  getPackagesList,
  getOrganicPackagesList,
  getPackagesListByFilter,
  getPackagesListBySearch,
} from "../../../Services/Packages.service";
import SharedDataContext from "../../../_helper/SharedData";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import {
  Box,
  styled,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Skeleton,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { packageFilterColumns } from "../../../Data/Configuration/Packages";
import { showAlert } from "../../../Services/Shared.service";
import { useNavigate } from "react-router";
import { useTariffSearch } from "../../../_helper/TariffSearchContext/TariffSeachContext";

const MAX_PACKAGE_LIMIT = 20;

function PackagesList() {
  const {
    searchData,
    setSearchData,
    filterData,
    setFilterData,
    filterType,
    setFilterType,
    packagesPagination,
    setPackagesPagination,
    bulkEditMode,
    setBulkEditMode,
    selectedPackages,
    setSelectedPackages,
  } = useTariffSearch();
  const { globalCityField,allCityData } = useContext(SharedDataContext);
  const [packagesList, setPackagesList] = useState([]);
  const [loading, setLoading] = useState(false);
  // const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 10 });
  const [count, setCount] = useState();
  const [isOpenEditClientModal, setIsOpenEditClientModal] = useState(false);
  const navigate = useNavigate();
  const prevCityRef = useRef(globalCityField.value?.id);

  // A stable function to reset page and fetch
  const resetPageAndFetch = () => {
    setPackagesPagination(prev => {
      if (prev.page === 0) {
        // Already page 0 → just fetch
        fetchPackagesData();
        return prev;
      }
      // Set page to 0 (main effect will fetch)
      return { ...prev, page: 0 };
    });
  };

  useEffect(() => {
    const cityChanged = prevCityRef.current !== globalCityField.value?.id;
    prevCityRef.current = globalCityField.value?.id;

    // Check child return state
    const isReturning = sessionStorage.getItem("packagesListNavToChild") === "true";
    if (isReturning) {
      sessionStorage.removeItem("packagesListNavToChild");
      console.log("Returning from child");
      fetchPackagesData();
      return;
    }

    // City changed → reset pagination to 0 first
    if (cityChanged) {
      resetPageAndFetch();
      return;
    }

    // Normal fetch when page changes or per-page changes
    fetchPackagesData();
  }, [
    globalCityField.value?.id,
    packagesPagination.page,
    packagesPagination.rowsPerPage
  ]);

  // Search change
  useEffect(() => {
    if (!searchData && filterType !== "search") return;
    resetPageAndFetch();
  }, [searchData]);

  // Filter change
  useEffect(() => {
    if (filterData.length === 0 && filterType !== "filter") return;
    resetPageAndFetch();
  }, [filterData]);


  useEffect(() => {
    // Clear bulk edit mode on component load
    setBulkEditMode(false);
    setSelectedPackages([]);
    sessionStorage.removeItem('packageBulkEditAccess');
  }, []); 

  const handleChangePage = (event, newPage) => {
    setPackagesPagination((prev) => ({ ...prev, page: newPage }));
  };
  // const totalPages = Math.floor(count / pagination.rowsPerPage);

  const handleChangeRowsPerPage = (event) => {
    setPackagesPagination({
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10),
    });
  };

  const getCarNameById = (carId) => {
  if (!Array.isArray(allCityData)) return "-";

  for (const city of allCityData) {
    const match = city?.carType?.find(car => car.id === carId);
    if (match) return match.name;
  }

  return "-";
};
  const fetchPackagesData = () => {
    setLoading(true);
    const serviceCityId = globalCityField.value?.id || null;
    if (filterData.length == 0 && !searchData) {
      let obj = {
        // isPagination: false,
        serviceCityId: serviceCityId,
        page: packagesPagination.page,
        size: packagesPagination.rowsPerPage,
      };

      getPackagesList(obj)
        .then((result) => {
          setPackagesList(result.data.list);
          setCount(result.data.count);
        })
        .catch((err) => {
          showAlert(
            "error",
            err.response?.data?.message
              ? err.response.data.message
              : "Something went wrong"
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (filterData.length > 0 && filterType == "filter") {
      let obj = {
        serviceCityId: serviceCityId,
        page: packagesPagination.page,
        size: packagesPagination.rowsPerPage,
      };
      filterData.forEach((d) => {
        // Use filterValues for API (contains ID), values is for display (contains label)
        obj[d.column_name] = d["filterValues"] ? d["filterValues"][0] : d["values"][0];
      });
      getPackagesListByFilter(obj)
        .then((result) => {
          setPackagesList(result.data.searchResponse);
          setCount(result.data.count);
        })
        .catch((err) => {
          showAlert(
            "error",
            err.response?.data?.message
              ? err.response.data.message
              : "Something went wrong"
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (searchData && filterType == "search") {
      let obj = {
        serviceCityId: serviceCityId,
        name: searchData,
        page: packagesPagination.page,
        size: packagesPagination.rowsPerPage,
      };
      getPackagesListBySearch(obj)
        .then((result) => {
          setPackagesList(result.data.list);
          setCount(result.data.count);
        })
        .catch((err) => {
          showAlert(
            "error",
            err.response?.data?.message
              ? err.response.data.message
              : "Something went wrong"
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  let columns = [];
  packageFilterColumns.map((element) => {
    if (element.value) {
      columns.push({
        value: {
          name: element.name,
          value: element.value,
          type: element.type,
        },
        label: element.name,
      });
    }
  });

  const removeFilter = (index) => {
    const newfilterData = [...filterData];
    newfilterData.splice(index, 1);
    setFilterData(newfilterData);
  };

  const handleCheckboxChange = (pkg, isChecked) => {
    if (isChecked) {
      if (selectedPackages.length >= MAX_PACKAGE_LIMIT) {
        showAlert("warning", `Maximum ${MAX_PACKAGE_LIMIT} packages can be selected for bulk edit`);
        return;
      }
      setSelectedPackages([...selectedPackages, pkg]);
    } else {
      setSelectedPackages(selectedPackages.filter(p => p.id !== pkg.id));
    }
  };

  const isPackageSelected = (pkgId) => {
    return selectedPackages.some(p => p.id === pkgId);
  };

  const IOSSwitch = styled((props) => (
    <Switch
      focusVisibleClassName=".Mui-focusVisible"
      disableRipple
      {...props}
    />
  ))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    "& .MuiSwitch-switchBase": {
      padding: 0,
      margin: 2,
      transitionDuration: "300ms",
      "&.Mui-checked": {
        transform: "translateX(16px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          backgroundColor: "#65C466",
          opacity: 1,
          border: 0,
          ...theme.applyStyles("dark", {
            backgroundColor: "#2ECA45",
          }),
        },
      },
      "&.Mui-focusVisible .MuiSwitch-thumb": {
        color: "#33cf4d",
        border: "6px solid #fff",
      },
      "&.Mui-disabled .MuiSwitch-thumb": {
        color: theme.palette.grey[100],
        ...theme.applyStyles("dark", {
          color: theme.palette.grey[600],
        }),
      },
    },
    "& .MuiSwitch-thumb": {
      boxSizing: "border-box",
      width: 22,
      height: 22,
    },
    "& .MuiSwitch-track": {
      borderRadius: 26 / 2,
      backgroundColor: "#E9E9EA",
      opacity: 1,
      transition: theme.transitions.create(["background-color"], {
        duration: 500,
      }),
      ...theme.applyStyles("dark", {
        backgroundColor: "#39393D",
      }),
    },
  }));

  const cancelButtonStyle = {
    color: "rgba(172, 45, 45, 1)",
    border: "1px solid rgba(181, 57, 57, 1)",
    borderRadius: "6px",
    backgroundColor: "rgba(244, 225, 225, 1)",
    height: "3.06vw",
    fontSize: "1.11vw",
    padding: "0.69vw 0.97vw",
    textTransform: "none",
    "&:hover": {
      color: "rgba(251, 0, 0, 1)",
      backgroundColor: "rgba(244, 225, 225, 1)",
      borderColor: "#cc4141ff",
    },
  };

  const greenButtonStyle = {
    color: "rgba(57, 181, 74, 1)",
    border: "1px solid rgba(57, 181, 74, 1)",
    borderRadius: "6px",
    backgroundColor: "rgba(225, 244, 228, 1)",
    height: "3.06vw",
    fontSize: "1.11vw",
    padding: "0.69vw 0.97vw",
    textTransform: "none",
    "&:hover": {
      color: "rgba(57, 181, 74, 1)",
      backgroundColor: "rgba(225, 244, 228, 1)",
      borderColor: "#41CC7D",
    },
  };

  return (
    <Box sx={{ width: "100%" }}>
      {loading || packagesList?.length > 0 ? (
        <>
          <TableContainer
            component={Paper}
            aria-label="sticky table"
            sx={{
              maxHeight: "40vw",
              scrollbarWidth: "none", // Firefox
              "&::-webkit-scrollbar": {
                display: "none", // Chrome, Safari, Edge
              },
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      color: "#626262",
                      fontWeight: "700",
                      fontSize: "1.11vw",
                      height: "3.89vw",
                      maxHeight: "5vw",
                      paddingX: 0,
                      paddingY: "10px",
                      backgroundColor: "#E1F4E4",
                    },
                  }}
                >
                  {bulkEditMode && (
                    <TableCell>
                      <Box
                        sx={{
                          textAlign: "left",
                          paddingX: "10px",
                        }}
                      >
                        Select ({selectedPackages.length}/{MAX_PACKAGE_LIMIT})
                      </Box>
                    </TableCell>
                  )}
                  <TableCell>
                    <Box
                      sx={{
                        textAlign: "left",
                        paddingX: "10px",
                      }}
                    >
                      Package Name
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        textAlign: "left",
                        paddingX: "10px",
                      }}
                    >
                      Service City
                    </Box>
                  </TableCell>
                  {/* <TableCell align="left">Service City</TableCell> */}
                  <TableCell align="left">Initial Distance(Km)</TableCell>
                  <TableCell align="left">Initial Distance Charge</TableCell>
                  <TableCell align="left">Per Km Charge</TableCell>
                  <TableCell align="left">Available On</TableCell>
                  <TableCell align="left">Availability</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading
                  ? [...Array(7)].map((_, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Skeleton animation="wave" width="100%" height={30} />
                        </TableCell>
                        <TableCell>
                          <Skeleton animation="wave" width="100%" height={30} />
                        </TableCell>
                        <TableCell>
                          <Skeleton animation="wave" width="100%" height={30} />
                        </TableCell>
                        <TableCell>
                          <Skeleton animation="wave" width="100%" height={30} />
                        </TableCell>
                        <TableCell>
                          <Skeleton animation="wave" width="100%" height={30} />
                        </TableCell>
                        <TableCell>
                          <Skeleton animation="wave" width="100%" height={30} />
                        </TableCell>
                        <TableCell>
                          <Skeleton animation="wave" width="100%" height={30} />
                        </TableCell>
                      </TableRow>
                    ))
                  : packagesList.map((singlePackageData) => (
                      <TableRow
                        key={singlePackageData.id}
                        sx={{
                          "&:hover": {
                            cursor: "pointer",
                            backgroundColor: "#EBEBEB",
                          },
                          "&:last-child td, &:last-child th": { border: 0 },
                          "& td": {
                            fontSize: "1.11vw",
                            paddingX: "8px",
                            borderBottom: "none",
                          },
                        }}
                        onClick={(e) => {
                          // Toggle checkbox when in bulk edit mode
                          if (bulkEditMode) {
                            const isCurrentlySelected = isPackageSelected(singlePackageData.id);
                            handleCheckboxChange(singlePackageData, !isCurrentlySelected);
                            return;
                          }
                          // Set flag to preserve pagination when returning
                          sessionStorage.setItem('packagesListNavToChild', 'true');
                          navigate(
                            `${process.env.PUBLIC_URL}/tariff_master/view/packages/${singlePackageData.id}`
                          );
                        }}
                      >
                        {bulkEditMode && (
                          <TableCell>
                            <Checkbox
                              checked={isPackageSelected(singlePackageData.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleCheckboxChange(singlePackageData, e.target.checked);
                              }}
                              sx={{
                                color: "#39B54A",
                                "&.Mui-checked": {
                                  color: "#39B54A",
                                },
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell sx={{maxWidth:"9.94vw"}}>
                          <Box
                            sx={{
                              textAlign: "left",
                              fontSize: "1vw",
                              color: "#39B54A",
                              fontWeight: "600",
                              padding:'0.6vw',
                            }}
                          >
                            {singlePackageData.name}
                          </Box>
                        </TableCell>
                        <TableCell align="left">
                          <Box
                            sx={{
                              textAlign: "left",
                              fontSize: "1vw",
                              color: "rgba(49, 49, 49, 1)",
                              fontWeight: "600",
                            }}
                          >
                            {singlePackageData.serviceCityName
                              ? singlePackageData.serviceCityName
                              : "-"}
                          </Box>
                        </TableCell>
                        <TableCell align="left">
                          <Box
                            sx={{
                              textAlign: "left",
                              fontSize: "1vw",
                              color: "#313131",
                            }}
                          >
                            {singlePackageData.initialDistance ?? 0}
                          </Box>
                        </TableCell>

                        <TableCell align="left">
                          <Box
                            sx={{
                              textAlign: "left",
                              color: "black",
                              fontWeight: "bold",
                              fontSize: "0.97vw",
                              padding: "12px 8px",
                            }}
                          >
                            {singlePackageData.initialDistanceCharge ?? 0}
                          </Box>
                        </TableCell>

                        <TableCell align="left">
                          <Box
                            sx={{
                              textAlign: "left",
                              fontSize: "0.97vw",
                              color: "#313131",
                              fontWeight: "400",
                            }}
                          >
                            {singlePackageData.perKmCharge ?? 0}
                          </Box>
                        </TableCell>

                        <TableCell align="center">
                          <Box
                            sx={{
                              textAlign: "left",
                              fontSize: "1vw",
                              color: "#313131",
                              fontWeight: "bold",
                            }}
                          >
                            {singlePackageData.isKiosk
                              ? "Kiosk"
                              : "TMS"}
                          </Box>
                        </TableCell>

                        <TableCell align="center">
                          <Box display="flex" alignItems="center" gap={1}>
                            {/* <IOSSwitch
                              checked={singlePackageData.isActive}
                              disabled
                            />
                            <Typography>
                              {singlePackageData.isActive ? "Yes" : "No"}
                            </Typography> */}
                            <Box
                              sx={{
                                flex: 1,
                                fontSize: "1vw",
                                color: "#313131",
                                fontWeight: "bold",
                              }}
                            >
                              {singlePackageData.isActive ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    backgroundColor: "#E3F5E1",
                                    width: "6.11vw",
                                    height: "2.01vw",
                                    justifyContent: "space-around",
                                    alignItems: "center",
                                    borderRadius: "6px",
                                    padding: "6px",
                                    border: "1px solid rgba(116, 206, 106, 1)",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "0.56vw",
                                      height: "0.56vw",
                                      background: "rgba(116, 206, 106, 1)",
                                      borderRadius: "50%",
                                    }}
                                  />
                                  <Typography sx={{ fontSize: "0.97vw" }}>
                                    Available
                                  </Typography>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-around",
                                    width: "7vw",
                                    height: "2.01vw",
                                    borderRadius: "6px",
                                    padding: "6px",
                                    backgroundColor: "#ffe5e5", // light red
                                    border: "1px solid #cc0000", // darker red border
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "0.56vw",
                                      height: "0.56vw",
                                      backgroundColor: "#cc0000",
                                      borderRadius: "50%",
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "0.97vw",
                                      color: "#cc0000",
                                    }}
                                  >
                                    Unavailable
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={count}
            page={packagesPagination.page}
            onPageChange={handleChangePage}
            rowsPerPage={packagesPagination.rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[7, 10, 25, 50]}
            sx={{
              "& .MuiTablePagination-toolbar": {
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                width: "100%",
                gap: 2,
                textAlign: "center",
                overflowX: "hidden",
              },
              // dropdown style
              "& .MuiTablePagination-select": {
                backgroundColor: "rgba(255, 255, 255, 1)",
                borderRadius: "6px",
                paddingX: "8px",
                paddingY: "4px",
                border: "1px solid #D9D9D9",
              },

              // arrows bigger and green
              "& .MuiTablePagination-actions button": {
                color: "rgba(112, 112, 112, 1)",
                transform: "scale(1.3)",
              },

              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  color: "rgba(98, 98, 98, 1)",
                  fontSize: "0.97vw",
                  textAlign: "center",
                  m: "auto",
                  letterSpacing: "0.02vw",
                },
            }}
          />{" "}
        </>
      ) : (
        <Box
          sx={{
            width: "100%",
            backgroundColor: "white",
            minHeight: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "6px",
          }}
        >
          <Typography sx={{ color: "black" }}>No Packages Available</Typography>
        </Box>
      )}
    </Box>
  );
}

export default PackagesList;
